<template>
  <v-dialog v-model="internalOpen" max-width="920" scrollable>
    <v-card class="agenda-config-dialog">
      <!-- Cabeçalho -->
      <v-card-title class="ds-dialog-header">
        <v-icon left color="white">mdi-calendar-clock</v-icon>
        <span class="ds-dialog-title">Configurar agenda</span>
        <v-spacer></v-spacer>
        <v-btn icon dark aria-label="Ver tour" @click="startTour">
          <v-icon>mdi-help-circle-outline</v-icon>
        </v-btn>
        <v-btn class="close-btn" icon dark @click="internalOpen = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <div class="dialog-subhead" data-tour="ac-clinica">
        <v-icon size="16" color="primary">mdi-hospital-building</v-icon>
        <span class="dialog-subhead__clinic">{{ clinicName || "Clínica" }}</span>
        <span class="dialog-subhead__hint">
          Indique em quais dias e horários você atende nesta clínica. Esses
          períodos aparecem destacados na agenda.
        </span>
      </div>

      <!-- Banner de observações de sombreamento (persistentes) -->
      <v-alert
        v-if="overlapNotes.length"
        type="warning"
        text
        dense
        border="left"
        class="overlap-banner mx-4 mt-4 mb-0"
      >
        <div class="font-weight-bold mb-1">
          Sobreposições de disponibilidade registradas:
        </div>
        <ul class="pl-4 mb-0">
          <li v-for="n in overlapNotes" :key="n.id">{{ n.note }}</li>
        </ul>
      </v-alert>

      <v-tabs
        v-model="tab"
        background-color="transparent"
        color="primary"
        grow
        class="flex-grow-0"
      >
        <v-tab data-tour="ac-tab-disp">
          <v-icon left small>mdi-calendar-check</v-icon>Disponibilidades
        </v-tab>
        <v-tab data-tour="ac-tab-travas">
          <v-icon left small>mdi-calendar-lock</v-icon>Bloqueios
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-card-text class="ds-dialog-body">
        <div v-if="loading" class="loading-hint">
          <v-progress-circular indeterminate color="primary" size="28" />
        </div>

        <v-tabs-items v-else v-model="tab" class="transparent">
          <!-- ═══════════ DISPONIBILIDADES ═══════════ -->
          <v-tab-item>
            <div>
              <!-- Formulário -->
              <section class="ds-form-section" data-tour="ac-nova-disp">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-calendar-plus</v-icon>
                    <span>Nova disponibilidade</span>
                  </div>
                </div>
                <v-form ref="availForm" v-model="availValid">
                  <v-row dense>
                    <v-col cols="6" md="6">
                      <v-text-field
                        v-model="availForm.time_from"
                        type="time"
                        label="De"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[(v) => !!v || 'Obrigatório']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" md="6">
                      <v-text-field
                        v-model="availForm.time_to"
                        type="time"
                        label="Até"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[
                          (v) => !!v || 'Obrigatório',
                          (v) =>
                            !availForm.time_from ||
                            v > availForm.time_from ||
                            'Deve ser após o início',
                        ]"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <div class="field-label mt-3 mb-1">Dias da semana</div>
                  <v-chip-group
                    data-tour="ac-dias"
                    v-model="availForm.weekdays"
                    multiple
                    column
                    active-class="primary--text"
                  >
                    <v-chip
                      v-for="d in WEEKDAYS"
                      :key="d.value"
                      :value="d.value"
                      filter
                      outlined
                      small
                      color="primary"
                    >
                      {{ d.label }}
                    </v-chip>
                  </v-chip-group>

                  <v-row dense class="mt-2">
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field
                        v-model="availForm.date_start"
                        type="date"
                        label="Válida a partir de"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[(v) => !!v || 'Obrigatório']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field
                        v-model="availForm.date_end"
                        type="date"
                        label="Válida até (opcional)"
                        outlined
                        dense
                        clearable
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center">
                      <v-switch
                        v-model="availForm.applies_on_holiday"
                        inset
                        dense
                        hide-details
                        class="mt-0 pt-0"
                        :label="
                          availForm.applies_on_holiday
                            ? 'Atende em feriados'
                            : 'Bloqueia feriados'
                        "
                      ></v-switch>
                    </v-col>
                  </v-row>

                  <div class="text-right mt-3">
                    <v-btn
                      color="primary"
                      depressed
                      :loading="savingAvail"
                      :disabled="!availForm.weekdays.length"
                      @click="submitAvailability"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      {{ editingAvailId ? "Salvar" : "Adicionar" }}
                    </v-btn>
                    <v-btn
                      v-if="editingAvailId"
                      text
                      class="ml-2"
                      @click="resetAvailForm"
                    >
                      Cancelar
                    </v-btn>
                  </div>
                </v-form>
              </section>

              <!-- Lista -->
              <section class="ds-form-section">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-calendar-check</v-icon>
                    <span>Disponibilidades</span>
                  </div>
                </div>
                <div v-if="!availabilities.length" class="empty-hint">
                  Nenhuma disponibilidade cadastrada ainda.
                </div>
                <div v-else class="rows" data-tour="ac-lista-disp">
                  <div
                    v-for="a in availabilities"
                    :key="a.id"
                    class="avail-row"
                    :class="{
                      'avail-row--overlap': a.overlap_note,
                      'avail-row--inactive': !a.active,
                    }"
                  >
                    <div class="avail-row__main">
                      <div class="avail-row__days">
                        <v-chip
                          v-for="d in a.weekdays"
                          :key="d"
                          x-small
                          color="primary"
                          outlined
                          class="mr-1"
                        >
                          {{ weekdayLabel(d) }}
                        </v-chip>
                        <span class="avail-row__time">
                          {{ a.time_from }} às {{ a.time_to }}
                        </span>
                        <v-chip
                          v-if="!a.active"
                          x-small
                          color="grey"
                          outlined
                          class="ml-1"
                        >
                          Suspensa
                        </v-chip>
                      </div>
                      <div class="avail-row__meta">
                        <span>{{ vigencia(a) }}</span>
                        <span class="dot">•</span>
                        <span>
                          {{
                            a.applies_on_holiday
                              ? "Atende em feriados"
                              : "Bloqueia feriados"
                          }}
                        </span>
                      </div>
                      <div v-if="a.overlap_note" class="avail-row__note">
                        <v-icon size="13" color="warning">mdi-alert</v-icon>
                        {{ a.overlap_note }}
                      </div>
                    </div>
                    <div class="avail-row__actions">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            v-bind="attrs"
                            v-on="on"
                            :loading="togglingId === a.id"
                            @click="toggleActive(a)"
                          >
                            <v-icon size="18" :color="a.active ? 'primary' : 'grey'">
                              {{
                                a.active
                                  ? "mdi-pause-circle-outline"
                                  : "mdi-play-circle-outline"
                              }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{
                          a.active ? "Suspender temporariamente" : "Reativar"
                        }}</span>
                      </v-tooltip>
                      <v-btn icon small @click="editAvailability(a)">
                        <v-icon size="18">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon small @click="removeAvailability(a)">
                        <v-icon size="18" color="error">mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </v-tab-item>

          <!-- ═══════════ TRAVAS ═══════════ -->
          <v-tab-item>
            <div>
              <section class="ds-form-section">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-calendar-lock</v-icon>
                    <span>Novo bloqueio</span>
                  </div>
                </div>
                <v-form ref="blockForm" v-model="blockValid">
                  <v-row dense>
                    <v-col cols="12">
                      <v-select
                        v-model="blockForm.reason"
                        :items="BLOCK_REASONS"
                        item-text="label"
                        item-value="value"
                        label="Motivo"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[(v) => !!v || 'Selecione o motivo']"
                      ></v-select>
                    </v-col>
                    <v-col v-if="blockForm.reason === 'outro'" cols="12">
                      <v-text-field
                        v-model="blockForm.reason_other"
                        label="Descreva o motivo"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[(v) => !!v || 'Descreva o motivo']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="blockForm.starts_at"
                        type="datetime-local"
                        label="Início"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[(v) => !!v || 'Obrigatório']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="blockForm.ends_at"
                        type="datetime-local"
                        label="Fim"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[
                          (v) => !!v || 'Obrigatório',
                          (v) =>
                            !blockForm.starts_at ||
                            v > blockForm.starts_at ||
                            'Deve ser após o início',
                        ]"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <div class="text-right mt-3">
                    <v-btn
                      color="primary"
                      depressed
                      :loading="savingBlock"
                      @click="submitBlock"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      {{ editingBlockId ? "Salvar alterações" : "Adicionar bloqueio" }}
                    </v-btn>
                    <v-btn
                      v-if="editingBlockId"
                      text
                      class="ml-2"
                      @click="resetBlockForm"
                    >
                      Cancelar
                    </v-btn>
                  </div>
                </v-form>
              </section>

              <section class="ds-form-section">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-lock</v-icon>
                    <span>Bloqueios</span>
                  </div>
                </div>
                <div v-if="!blocks.length" class="empty-hint">
                  Nenhum bloqueio cadastrado.
                </div>
                <div v-else class="rows">
                  <div v-for="b in blocks" :key="b.id" class="block-row">
                    <div class="block-row__main">
                      <div class="block-row__reason">
                        <v-icon size="16" color="#37474F">mdi-lock</v-icon>
                        {{ reasonLabel(b) }}
                      </div>
                      <div class="block-row__period">
                        {{ formatDateTime(b.starts_at) }} →
                        {{ formatDateTime(b.ends_at) }}
                      </div>
                      <div class="block-row__clinic">
                        {{ b.clinic_name || "Todas as clínicas" }}
                      </div>
                    </div>
                    <div class="block-row__actions">
                      <v-btn
                        v-if="b.clinic_id"
                        icon
                        small
                        @click="editBlock(b)"
                      >
                        <v-icon size="18">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="b.clinic_id"
                        icon
                        small
                        @click="removeBlock(b)"
                      >
                        <v-icon size="18" color="error">mdi-delete-outline</v-icon>
                      </v-btn>
                      <v-chip
                        v-else
                        x-small
                        outlined
                        color="grey"
                        title="Bloqueio que vale para todas as clínicas — gerencie na tela geral."
                      >
                        Todas as clínicas
                      </v-chip>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <!-- Dialog de confirmação de sombreamento -->
      <v-dialog v-model="overlapDialog" max-width="520" scrollable>
        <v-card class="ds-dialog">
          <v-card-title class="ds-dialog-header ds-dialog-header--warn">
            <v-icon left color="white">mdi-alert</v-icon>
            <span class="ds-dialog-title">Sobreposição de disponibilidade</span>
          </v-card-title>
          <v-card-text class="ds-dialog-body">
            <section class="ds-form-section ds-form-section--warning">
              <p class="mb-2">
                Este horário se sobrepõe a disponibilidades em outra(s) clínica(s):
              </p>
              <ul class="pl-4">
                <li v-for="c in overlapConflicts" :key="c.id" class="mb-1">
                  <strong>{{ c.clinic_name }}</strong> —
                  {{ c.weekdays.map(weekdayLabel).join("/") }}
                  {{ c.time_from }} às {{ c.time_to }}
                </li>
              </ul>
              <p class="mb-0 mt-2 text-caption grey--text text--darken-1">
                Se aceitar, a disponibilidade será gravada e esta observação
                aparecerá sempre que você abrir esta tela.
              </p>
            </section>
          </v-card-text>
          <v-card-actions class="ds-dialog-actions">
            <v-spacer></v-spacer>
            <v-btn text @click="overlapDialog = false">Cancelar</v-btn>
            <v-btn
              color="warning"
              depressed
              :loading="savingAvail"
              @click="acceptOverlap"
            >
              Salvar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" dark timeout="3500">
        {{ snackbar.text }}
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  getAvailabilities,
  createAvailability,
  updateAvailability,
  deleteAvailability,
  getBlocks,
  createBlock,
  updateBlock,
  deleteBlock,
  WEEKDAYS,
  BLOCK_REASONS,
} from "@/services/doctorScheduleService";
import tourDialogMixin from "@/features/tour/tourDialogMixin";
import { formatDate, formatDateTime } from "@/utils/datetime";

/**
 * Configuração de agenda ESCOPADA A UMA CLÍNICA (modal).
 * Abre a partir do card da clínica em MyClinics — o clinic_id vem por prop, não
 * há mais select de clínica. Mostra o histórico de disponibilidades/travas dessa
 * clínica e permite cadastrar novas.
 */
export default {
  name: "AgendaConfigDialog",

  mixins: [tourDialogMixin],

  props: {
    value: { type: Boolean, default: false },
    clinicId: { type: [Number, String], default: null },
    clinicName: { type: String, default: "" },
  },

  data() {
    return {
      WEEKDAYS,
      BLOCK_REASONS,
      tab: 0,
      loading: false,
      availabilities: [],
      overlapNotes: [],
      blocks: [],

      availForm: this.blankAvailForm(),
      availValid: false,
      editingAvailId: null,
      savingAvail: false,
      togglingId: null,

      overlapDialog: false,
      overlapConflicts: [],
      pendingAvailPayload: null,

      blockForm: this.blankBlockForm(),
      blockValid: false,
      editingBlockId: null,
      savingBlock: false,

      snackbar: { show: false, text: "", color: "success" },
    };
  },

  computed: {
    tourId() {
        // salvia-763: o rótulo virou "Configurar agenda", mas este id NÃO
        // muda. Ele é a chave de persistência do tour (`tours_seen`) — renomear
        // faria o tour reaparecer para todo mundo que já o viu.
        return "dialog-parametrizar-agenda";
    },
    internalOpen: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.tab = 0;
        this.resetAvailForm();
        this.resetBlockForm();
        this.initialize();
        this.tourOnOpen(); // tour guiado na 1ª vez que abre
      }
    },
  },

  methods: {
    formatDate,
    formatDateTime,
    tourReady() {
      return !this.loading;
    },
    tourSteps() {
      return [
        {
          title: "Configurar agenda 📅",
          description:
            "Aqui você define quando atende nesta clínica. Isso alimenta o destaque de dias/horários na hora de marcar atendimentos. Vou mostrar cada parte.",
        },
        {
          anchor: "ac-clinica",
          title: "Clínica em foco",
          description:
            "Esta configuração vale só para a clínica indicada aqui no topo — cada clínica tem a sua própria agenda de atendimento.",
        },
        {
          anchor: "ac-tab-disp",
          title: "Aba Disponibilidades",
          description:
            "Nesta aba você cadastra os períodos em que atende: os horários e dias da semana em que a clínica tem a sua agenda aberta.",
        },
        {
          anchor: "ac-nova-disp",
          title: "Nova disponibilidade",
          description:
            "Preencha o horário (de/até), escolha os dias da semana, defina a vigência (a partir de quando, e até quando se quiser) e se atende em feriados. Depois clique em Adicionar.",
        },
        {
          anchor: "ac-dias",
          title: "Dias da semana",
          description:
            "Marque os dias em que você atende naquele horário. Ex.: seg a sex das 08:00 às 12:00. Dá para cadastrar vários blocos diferentes.",
        },
        {
          anchor: "ac-lista-disp",
          title: "Disponibilidades cadastradas",
          description:
            "Suas disponibilidades aparecem aqui. Você pode suspender temporariamente (sem apagar), editar ou remover cada uma.",
        },
        {
          anchor: "ac-tab-travas",
          title: "Aba Bloqueios",
          description:
            "Bloqueios são períodos indisponíveis (férias, congresso, etc.). Nesta aba você os cadastra com motivo e intervalo de datas — durante eles a agenda fica fechada, mesmo que haja disponibilidade.",
        },
      ];
    },

    blankAvailForm() {
      return {
        clinic_id: this.clinicId,
        weekdays: [],
        time_from: "",
        time_to: "",
        date_start: "",
        date_end: null,
        applies_on_holiday: true,
        active: true,
      };
    },
    blankBlockForm() {
      return {
        clinic_id: this.clinicId,
        reason: "",
        reason_other: "",
        starts_at: "",
        ends_at: "",
      };
    },

    async initialize() {
      this.loading = true;
      try {
        await Promise.all([this.loadAvailabilities(), this.loadBlocks()]);
      } catch (e) {
        this.notify("Erro ao carregar a configuração.", "error");
      } finally {
        this.loading = false;
      }
    },

    // Só interessa esta clínica.
    sameClinic(id) {
      return Number(id) === Number(this.clinicId);
    },

    async loadAvailabilities() {
      const { items, overlapNotes } = await getAvailabilities();
      this.availabilities = (items || []).filter((a) =>
        this.sameClinic(a.clinic_id),
      );
      // Só as observações que citam esta clínica.
      this.overlapNotes = (overlapNotes || []).filter(
        (n) => n.clinic_id == null || this.sameClinic(n.clinic_id),
      );
    },
    async loadBlocks() {
      const all = await getBlocks();
      // Travas desta clínica + as que valem para todas (clinic_id null).
      this.blocks = (all || []).filter(
        (b) => b.clinic_id == null || this.sameClinic(b.clinic_id),
      );
    },

    // ─── Disponibilidades ───────────────────────────────────────────
    availPayload() {
      return {
        clinic_id: this.clinicId,
        weekdays: this.availForm.weekdays,
        time_from: this.availForm.time_from,
        time_to: this.availForm.time_to,
        date_start: this.availForm.date_start,
        date_end: this.availForm.date_end || null,
        applies_on_holiday: this.availForm.applies_on_holiday,
        active: this.availForm.active,
      };
    },

    async submitAvailability() {
      if (!this.$refs.availForm.validate() || !this.availForm.weekdays.length) {
        if (!this.availForm.weekdays.length)
          this.notify("Selecione ao menos um dia da semana.", "error");
        return;
      }
      this.savingAvail = true;
      try {
        const payload = this.availPayload();
        const fn = this.editingAvailId
          ? (p) => updateAvailability(this.editingAvailId, p)
          : createAvailability;
        const res = await fn(payload);

        if (res.overlap) {
          this.overlapConflicts = res.conflicts;
          this.pendingAvailPayload = payload;
          this.overlapDialog = true;
          return;
        }
        await this.afterAvailSaved();
      } catch (e) {
        this.notify("Erro ao salvar a disponibilidade.", "error");
      } finally {
        this.savingAvail = false;
      }
    },

    async acceptOverlap() {
      this.savingAvail = true;
      try {
        const payload = { ...this.pendingAvailPayload, accept_overlap: true };
        const fn = this.editingAvailId
          ? (p) => updateAvailability(this.editingAvailId, p)
          : createAvailability;
        await fn(payload);
        this.overlapDialog = false;
        await this.afterAvailSaved();
      } catch (e) {
        this.notify("Erro ao salvar a disponibilidade.", "error");
      } finally {
        this.savingAvail = false;
      }
    },

    async afterAvailSaved() {
      this.notify(
        this.editingAvailId
          ? "Disponibilidade atualizada."
          : "Disponibilidade adicionada.",
      );
      this.resetAvailForm();
      await this.loadAvailabilities();
    },

    editAvailability(a) {
      this.editingAvailId = a.id;
      this.availForm = {
        clinic_id: a.clinic_id,
        weekdays: [...a.weekdays],
        time_from: a.time_from,
        time_to: a.time_to,
        date_start: a.date_start,
        date_end: a.date_end,
        applies_on_holiday: a.applies_on_holiday,
        active: a.active,
      };
    },

    // Suspende/reativa sem perder o cadastro (campo active).
    async toggleActive(a) {
      this.togglingId = a.id;
      try {
        await updateAvailability(a.id, {
          clinic_id: a.clinic_id,
          weekdays: a.weekdays,
          time_from: a.time_from,
          time_to: a.time_to,
          date_start: a.date_start,
          date_end: a.date_end || null,
          applies_on_holiday: a.applies_on_holiday,
          active: !a.active,
          // Preserva um sombreamento já aceito, para não reabrir o alerta.
          accept_overlap: !!a.overlap_note,
        });
        this.notify(
          a.active ? "Disponibilidade suspensa." : "Disponibilidade reativada.",
        );
        await this.loadAvailabilities();
      } catch (e) {
        this.notify("Erro ao alterar a disponibilidade.", "error");
      } finally {
        this.togglingId = null;
      }
    },

    async removeAvailability(a) {
      const dias = (a.weekdays || []).map(this.weekdayLabel).join(", ");
      const ok = await this.$confirm({
        type: "delete",
        title: "Excluir disponibilidade",
        message: `Excluir a disponibilidade de <strong>${a.clinic_name}</strong> (${dias}, das ${a.time_from} às ${a.time_to})?`,
        hint: "Os atendimentos já agendados não são afetados.",
      });
      if (!ok) return;
      try {
        await deleteAvailability(a.id);
        this.notify("Disponibilidade removida.");
        await this.loadAvailabilities();
      } catch (e) {
        this.notify("Erro ao remover.", "error");
      }
    },

    resetAvailForm() {
      this.editingAvailId = null;
      this.availForm = this.blankAvailForm();
      this.pendingAvailPayload = null;
      this.$refs.availForm && this.$refs.availForm.resetValidation();
    },

    // ─── Travas ─────────────────────────────────────────────────────
    blockPayload() {
      return {
        clinic_id: this.clinicId,
        reason: this.blockForm.reason,
        reason_other:
          this.blockForm.reason === "outro" ? this.blockForm.reason_other : null,
        starts_at: this.blockForm.starts_at,
        ends_at: this.blockForm.ends_at,
      };
    },

    async submitBlock() {
      if (!this.$refs.blockForm.validate()) return;
      this.savingBlock = true;
      try {
        if (this.editingBlockId) {
          await updateBlock(this.editingBlockId, this.blockPayload());
          this.notify("Bloqueio atualizado.");
        } else {
          await createBlock(this.blockPayload());
          this.notify("Bloqueio adicionado.");
        }
        this.resetBlockForm();
        await this.loadBlocks();
      } catch (e) {
        this.notify("Erro ao salvar o bloqueio.", "error");
      } finally {
        this.savingBlock = false;
      }
    },

    editBlock(b) {
      this.editingBlockId = b.id;
      this.blockForm = {
        clinic_id: b.clinic_id,
        reason: b.reason,
        reason_other: b.reason_other || "",
        starts_at: this.toInputDateTime(b.starts_at),
        ends_at: this.toInputDateTime(b.ends_at),
      };
    },

    async removeBlock(b) {
      const ok = await this.$confirm({
        type: "delete",
        title: "Excluir bloqueio",
        message: `Excluir o bloqueio de <strong>${this.reasonLabel(
          b,
        )}</strong> (${this.formatDateTime(b.starts_at)} até ${this.formatDateTime(
          b.ends_at,
        )})?`,
        hint: "A agenda volta a seguir a disponibilidade cadastrada nesse período.",
      });
      if (!ok) return;
      try {
        await deleteBlock(b.id);
        this.notify("Bloqueio removido.");
        await this.loadBlocks();
      } catch (e) {
        this.notify("Erro ao remover.", "error");
      }
    },

    resetBlockForm() {
      this.editingBlockId = null;
      this.blockForm = this.blankBlockForm();
      this.$refs.blockForm && this.$refs.blockForm.resetValidation();
    },

    // ─── Helpers ────────────────────────────────────────────────────
    weekdayLabel(v) {
      const d = WEEKDAYS.find((w) => w.value === v);
      return d ? d.label : v;
    },
    reasonLabel(b) {
      if (b.reason === "outro") return b.reason_other || "Outro";
      const r = BLOCK_REASONS.find((x) => x.value === b.reason);
      return r ? r.label : b.reason;
    },
    vigencia(a) {
      const start = this.formatDate(a.date_start);
      return a.date_end
        ? `${start} até ${this.formatDate(a.date_end)}`
        : `A partir de ${start}`;
    },
    // ISO -> valor de input datetime-local ("YYYY-MM-DDTHH:mm") no fuso local.
    toInputDateTime(iso) {
      if (!iso) return "";
      const dt = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
        dt.getDate(),
      )}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    },
    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
.agenda-config-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-subhead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 20px;
  background: var(--ds-blue-50);
  border-bottom: 1px solid var(--ds-blue-100);
}
.dialog-subhead__clinic {
  font-size: 14px;
  font-weight: 700;
  color: var(--ds-text-strong);
}
.dialog-subhead__hint {
  flex: 1 1 260px;
  font-size: 12px;
  color: var(--ds-text-soft);
  line-height: 1.4;
}

.loading-hint {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-text-soft);
}

.empty-hint {
  text-align: center;
  color: var(--ds-text-muted);
  font-size: 13px;
  padding: 16px 0;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.avail-row,
.block-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid var(--ds-blue-100);
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.avail-row:hover,
.block-row:hover {
  box-shadow: 0 6px 16px rgba(31, 77, 63, 0.1);
  border-color: var(--ds-blue-200);
}
.avail-row--inactive {
  opacity: 0.6;
  background: #f7f7f7;
}
.avail-row--overlap {
  border-color: #ffcc80;
  background: #fff8ef;
}

.block-row__reason {
  font-size: 14px;
  font-weight: 700;
  color: var(--ds-text-strong);
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.avail-row__days {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}
.avail-row__time {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-primary);
  margin-left: 4px;
}
.avail-row__meta,
.block-row__period,
.block-row__clinic {
  font-size: 12px;
  color: var(--ds-text-soft);
}
.avail-row__meta .dot {
  margin: 0 6px;
  color: var(--ds-text-muted);
}
.avail-row__note {
  margin-top: 6px;
  font-size: 12px;
  color: #b9770e;
  display: flex;
  align-items: center;
  gap: 4px;
}
.block-row__period {
  font-weight: 600;
  color: var(--ds-text-700);
  margin: 2px 0;
}
.avail-row__actions,
.block-row__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Header do dialog de sombreamento (laranja de aviso, não o verde padrão) */
.ds-dialog-header.ds-dialog-header--warn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #fff;
}
.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
}
</style>
